import { Component, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { BaseModalComponent } from "../base-modal/base-modal.component";

/**
 * FLIP-78. `isRequired` selects the copy/colours/buttons (1A optional vs 1B required);
 * `blocking` is only ever true for a required update whose initial snooze has already
 * expired, and hides both the secondary pill and the header × so "Update now" is the
 * only way out.
 */
export interface DialogRefreshData {
  isRequired: boolean;
  blocking: boolean;
}

/** Result passed to `dialogRef.afterClosed()` so VersionCheckService knows whether to
 * record a new snooze - "update" reloads immediately so there's nothing to snooze. */
export type DialogRefreshResult = "update" | "snooze";

/**
 * Built directly on app-base-modal rather than the ConfirmationModalComponent wrapper:
 * 1A/1B each have their own top-to-bottom gradient and per-side pill colours (design
 * spec), which the wrapper's fixed riverbed/parchment styling doesn't support.
 */
@Component({
  selector: "app-dialog-refresh",
  templateUrl: "./dialog-refresh.component.html",
  styleUrls: ["./dialog-refresh.component.scss"],
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
})
export class DialogRefreshComponent {
  constructor(
    public dialogRef: MatDialogRef<DialogRefreshComponent, DialogRefreshResult>,
    @Inject(MAT_DIALOG_DATA) public data: DialogRefreshData
  ) {}

  get title(): string {
    return this.data.isRequired ? "update required" : "update available";
  }

  // 1A's body renders as two lines with a blank line between them (see the
  // white-space: pre-line rule in dialog-refresh.component.scss, which is what makes an
  // embedded "\n\n" actually produce a blank line instead of collapsing like normal
  // HTML whitespace).
  get body(): string {
    return this.data.isRequired
      ? "This update includes important changes that require everyone to use the latest version of flip*."
      : "A new version of flip* is available.\n\nUpdate now to access the latest improvements, bug fixes and new features.";
  }

  get primaryLabel(): string {
    return this.data.isRequired ? "Update Now" : "Install Now";
  }

  get secondaryLabel(): string {
    return this.data.isRequired ? "Wait 10 Minutes" : "Later";
  }

  /** Design spec: 1A is skylight → parchment, 1B (both its non-blocking and blocking
   * states) is blossom → parchment, top to bottom. */
  get backgroundGradient(): string {
    const from = this.data.isRequired ? "var(--color-blossom)" : "var(--color-skylight)";
    return `linear-gradient(to bottom, ${from}, var(--color-parchment))`;
  }

  /** Secondary/cancel (left) pill colour - the lighter of each variant's pair
   * (skylight for 1A's "Later", blossom for 1B's "Wait 10 minutes"), following the
   * app's standard confirm/cancel convention: confirm is the more prominent, darker
   * colour. Not shown at all once blocking. */
  get secondaryColor(): string {
    return this.data.isRequired ? "var(--color-blossom)" : "var(--color-skylight)";
  }

  /** Primary/confirm (right) pill colour - the darker of each variant's pair (harbour
   * for 1A, mulberry for 1B, including once blocking) - standard base-modal
   * convention puts confirm on the right in the more prominent colour. */
  get primaryColor(): string {
    return this.data.isRequired ? "var(--color-mulberry)" : "var(--color-harbour)";
  }

  /** harbour and mulberry are dark enough for parchment text; skylight and blossom are
   * light pastels that need dark (evergreen) text to stay readable. */
  private textColorFor(background: string): string {
    const darkBackgrounds = ["var(--color-harbour)", "var(--color-mulberry)"];
    return darkBackgrounds.includes(background)
      ? "var(--color-parchment)"
      : "var(--color-evergreen)";
  }

  get secondaryTextColor(): string {
    return this.textColorFor(this.secondaryColor);
  }

  get primaryTextColor(): string {
    return this.textColorFor(this.primaryColor);
  }

  updateNow(): void {
    this.dialogRef.close("update");
    location.reload();
  }

  snooze(): void {
    this.dialogRef.close("snooze");
  }
}
